import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useToast } from '@/app/components/ui/use-toast';
import { AUDIO_DURATION } from '@/app/constants';
import { useClickOutside } from '@/app/hooks';
import type { TimeTagElement } from '../../types';

interface TimeTagPopoverProps {
  timeTag: TimeTagElement;
  onChange: (start: number, end: number) => void;
  onClose: () => void;
}

export function TimeTagPopover({ timeTag, onChange, onClose }: TimeTagPopoverProps) {
  const { toast } = useToast();
  const t = useTranslations();

  const startValueRef = useRef(Math.floor(timeTag.start));
  const endValueRef = useRef(Math.floor(timeTag.end));
  const popoverRef = useRef<HTMLDivElement>(null);
  const isEditing = useRef(false);
  const positionRef = useRef<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  useLayoutEffect(() => {
    if (!timeTag.element) return;

    const rect = timeTag.element.getBoundingClientRect();
    positionRef.current = {
      top: rect.top + rect.height / 2,
      left: rect.right + 8,
    };

    if (popoverRef.current) {
      popoverRef.current.style.top = `${positionRef.current.top}px`;
      popoverRef.current.style.left = `${positionRef.current.left}px`;
    }
  }, [timeTag.element]);

  useEffect(() => {
    if (!isEditing.current) {
      startValueRef.current = Math.floor(timeTag.start);
      endValueRef.current = Math.min(Math.floor(timeTag.end), AUDIO_DURATION);

      const startInput = document.getElementById('time-tag-start') as HTMLInputElement;
      const endInput = document.getElementById('time-tag-end') as HTMLInputElement;

      if (startInput) startInput.value = String(Math.floor(timeTag.start));
      if (endInput) endInput.value = String(Math.min(Math.floor(timeTag.end), AUDIO_DURATION));
    }
  }, [timeTag.start, timeTag.end]);

  const handleClickOutside = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (isEditing.current) return;

      const target = event.target as Node;
      if (!timeTag.element.contains(target)) {
        onClose();
      }
    },
    [onClose, timeTag.element],
  );

  useClickOutside(popoverRef, handleClickOutside);

  const validateAndUpdateValue = (type: 'start' | 'end', value: string) => {
    const numValue = value === '' ? Number.NaN : Math.floor(Number(value));
    const input = document.getElementById(`time-tag-${type}`) as HTMLInputElement;

    // 处理无效输入
    if (Number.isNaN(numValue) || numValue < 0) {
      if (type === 'start') {
        input.value = String(startValueRef.current);
      } else {
        input.value = String(endValueRef.current);
      }
      return;
    }

    // 检查是否超出10秒限制
    if (numValue > AUDIO_DURATION) {
      if (type === 'start') {
        input.value = String(startValueRef.current);
      } else {
        input.value = String(endValueRef.current);
      }
      toast({
        type: 'warning',
        message: t('text2audio.timestampTextArea.timestampLimit', { limit: AUDIO_DURATION }),
      });
      return;
    }

    if (type === 'start') {
      // 如果开始时间等于 AUDIO_DURATION，恢复原值
      if (numValue === AUDIO_DURATION) {
        input.value = String(startValueRef.current);
        toast({
          type: 'warning',
          message: t('text2audio.timestampTextArea.startTimeOverDuration', { limit: AUDIO_DURATION }),
        });
        return;
      }

      // 如果开始时间大于结束时间，自动调整结束时间为开始时间+1
      if (numValue >= endValueRef.current) {
        const newStart = numValue;
        const newEnd = Math.min(newStart + 1, AUDIO_DURATION);
        startValueRef.current = newStart;
        endValueRef.current = newEnd;
        onChange(newStart, newEnd);

        input.value = String(newStart);
        const endInput = document.getElementById('time-tag-end') as HTMLInputElement;
        if (endInput) {
          endInput.value = String(newEnd);
        }
        toast({
          type: 'warning',
          message: t('text2audio.timestampTextArea.startTimeLaterEndTime'),
        });
      } else {
        // 正常情况：开始时间小于结束时间
        startValueRef.current = numValue;
        onChange(numValue, endValueRef.current);
        input.value = String(numValue);
      }
    } else {
      // 如果结束时间等于 0，恢复原值
      if (numValue === 0) {
        input.value = String(endValueRef.current);
        toast({
          type: 'warning',
          message: t('text2audio.timestampTextArea.endTimeEqualZero'),
        });
        return;
      }

      // 如果结束时间小于开始时间，自动调整开始时间为结束时间-1
      if (numValue <= startValueRef.current) {
        const newEnd = numValue;
        const newStart = Math.max(0, newEnd - 1);
        startValueRef.current = newStart;
        endValueRef.current = newEnd;
        onChange(newStart, newEnd);

        input.value = String(newEnd);
        const startInput = document.getElementById('time-tag-start') as HTMLInputElement;
        if (startInput) {
          startInput.value = String(newStart);
        }
        toast({
          type: 'warning',
          message: t('text2audio.timestampTextArea.endTimeOverEarlierTime'),
        });
      } else {
        // 正常情况：结束时间大于开始时间
        endValueRef.current = numValue;
        onChange(startValueRef.current, numValue);
        input.value = String(numValue);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, type: 'start' | 'end') => {
    if (e.key === 'Enter') {
      validateAndUpdateValue(type, (e.target as HTMLInputElement).value);
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <div
      ref={popoverRef}
      className="time-tag-popover -translate-y-1/2 before:-translate-y-1/2 fixed z-50 flex items-center gap-2 rounded-8 bg-system-bg02 p-2 before:absolute before:top-1/2 before:left-[-16px] before:h-0 before:w-0 before:border-[8px] before:border-transparent before:border-r-system-bg02 before:content-['']"
    >
      <div className="flex items-center gap-1">
        <input
          id="time-tag-start"
          type="number"
          title=""
          defaultValue={startValueRef.current}
          min={0}
          max={Math.min(endValueRef.current - 1, AUDIO_DURATION)}
          step={1}
          onInput={(e) => {
            const input = e.target as HTMLInputElement;
            if (input.value.includes('.')) {
              input.value = input.value.split('.')[0];
            }
          }}
          onKeyDown={(e) => handleKeyDown(e, 'start')}
          onBlur={(e) => {
            validateAndUpdateValue('start', e.target.value);
            isEditing.current = false;
          }}
          onFocus={() => {
            isEditing.current = true;
          }}
          className="flex h-8 w-10 items-center justify-center rounded-md bg-system-white/[.12] text-center font-normal text-sm text-system-white leading-[1.4] backdrop-blur-[30px]"
        />
        <span className="font-normal text-sm text-system-white/[.48] leading-[1.4]">s</span>
      </div>
      <span>-</span>
      <div className="flex items-center gap-1">
        <input
          id="time-tag-end"
          type="number"
          title=""
          defaultValue={endValueRef.current}
          min={startValueRef.current + 1}
          max={AUDIO_DURATION}
          step={1}
          onInput={(e) => {
            const input = e.target as HTMLInputElement;
            if (input.value.includes('.')) {
              input.value = input.value.split('.')[0];
            }
          }}
          onKeyDown={(e) => handleKeyDown(e, 'end')}
          onBlur={(e) => {
            validateAndUpdateValue('end', e.target.value);
            isEditing.current = false;
          }}
          onFocus={() => {
            isEditing.current = true;
          }}
          className="flex h-8 w-10 items-center justify-center rounded-md bg-system-white/[.12] text-center font-normal text-sm text-system-white leading-[1.4] backdrop-blur-[30px]"
        />
        <span className="font-normal text-sm text-system-white/[.48] leading-[1.4]">s</span>
      </div>
    </div>
  );
}
